/**
 * FIX-A1 (FR-03): stay facts for the fee-day calculator's cessation, death and
 * late-information zero classes, taken from governed records only.
 *
 * Sources (no free-text guessing):
 * - Exit: the first COMPLETED DEATH or DISCHARGE client transition out of
 *   residential care on or after the stay's admission day
 *   (`complete_residential_transition_controlled`). Mirrors
 *   `internal.residential_stay_exit_facts` (20435000003282).
 * - Informed dates: the active fee advice. The provider-informed date falls back
 *   to the advice issue date (policy `assumeProviderInformedFromSourceIssuedAt`).
 *   `dmta_informed_on` is the day the resident was told the means-tested amount
 *   (WS3: r 280-5(a)(iii) and r 285A-14(7)).
 *
 * Holds instead of guesses:
 * - A DISCHARGE with a null destination leaves "did not start at another approved
 *   home" (Rules r 280-5(a)(i), r 285A-14(8)) unknown. It only changes the
 *   bill when the home was told the contributions after the exit (or never), so
 *   only then is the day held: `CESSATION_DESTINATION_UNRECORDED`. Continuing
 *   home care does not establish the destination; an explicit governed boolean
 *   is required (20435000003288).
 * - r 285A-14(7) runs from the earlier of the stay start and the old-Act entry
 *   day. The controlled fee arrangement holds the evidenced old-Act entry day
 *   (FIX-D). When it is unknown and the resident was
 *   told within six months of this stay's start, an earlier entry day could
 *   still zero a day before the told date, so that day is held:
 *   `MTCF_OLD_ACT_ENTRY_UNRECORDED`. Told more than six months after the stay
 *   start, the zero applies whatever the earlier day was.
 *
 * Mirrors `internal.residential_fee_day_v3_stay_means` (20435000003282).
 */
import { addCalendarMonths } from './day-calculator';
import type { ResidentialFeeCohort } from './cohorts';
import { assertResidentialFeeDate } from './rates';

export type ResidentialStayExitType = 'DEATH' | 'DISCHARGE';

export interface ResidentialStayExit {
  transitionType: ResidentialStayExitType;
  ceasedOn: string;
  /** `client_transitions.to_service_type`. `SUPPORT_AT_HOME` is a RAC-only exit. */
  toServiceType: string | null;
  destinationIsResidential?: boolean | null;
}

export interface ResidentialStayExitRow {
  id: string;
  client_id: string;
  transition_type: string;
  effective_date: string | null;
  from_service_type: string | null;
  to_service_type: string | null;
  destination_is_residential?: boolean | null;
  status: string;
}

export type ResidentialStayHoldCode =
  | 'CESSATION_DESTINATION_UNRECORDED'
  | 'MTCF_OLD_ACT_ENTRY_UNRECORDED';

export interface ResidentialStayMeansFacts {
  mtcfInformedOn: string | null;
  oldActDayOfEntry?: string | null;
  ceasedOn?: string;
  ceasedWithoutStartingAnotherHome?: boolean;
  diedBeforeProviderInformed?: boolean;
}

export interface ResidentialStayMeansResult {
  facts: ResidentialStayMeansFacts;
  hold: ResidentialStayHoldCode | null;
}

/**
 * The first completed DEATH/DISCHARGE out of residential care on or after the
 * admission day. Same day: DEATH first, then the lower id.
 */
export function residentialStayExit(
  rows: readonly ResidentialStayExitRow[],
  clientId: string,
  admissionDate: string,
): ResidentialStayExit | null {
  assertResidentialFeeDate(admissionDate);
  let best: ResidentialStayExitRow | null = null;
  for (const row of rows) {
    if (row.client_id !== clientId || row.status !== 'COMPLETED') continue;
    if (row.transition_type !== 'DEATH' && row.transition_type !== 'DISCHARGE') continue;
    if (row.from_service_type !== 'RESIDENTIAL') continue;
    const date = row.effective_date?.slice(0, 10) ?? null;
    if (date === null || date < admissionDate) continue;
    if (best === null) {
      best = row;
      continue;
    }
    const bestDate = best.effective_date!.slice(0, 10);
    const rank = (value: ResidentialStayExitRow) => (value.transition_type === 'DEATH' ? 0 : 1);
    if (
      date < bestDate ||
      (date === bestDate &&
        (rank(row) < rank(best) || (rank(row) === rank(best) && row.id < best.id)))
    ) {
      best = row;
    }
  }
  if (best === null) return null;
  return {
    transitionType: best.transition_type as ResidentialStayExitType,
    ceasedOn: best.effective_date!.slice(0, 10),
    toServiceType: best.to_service_type,
    destinationIsResidential: best.destination_is_residential ?? null,
  };
}

export function residentialStayMeansFacts(args: {
  cohort: ResidentialFeeCohort;
  date: string;
  stayStartDay: string;
  providerInformedOn: string | null;
  sourceIssuedAt: string | null;
  dmtaInformedOn: string | null;
  oldActDayOfEntry?: string | null;
  exit: ResidentialStayExit | null;
}): ResidentialStayMeansResult {
  assertResidentialFeeDate(args.date);
  assertResidentialFeeDate(args.stayStartDay);
  const facts: ResidentialStayMeansFacts = { mtcfInformedOn: args.dmtaInformedOn };
  if (args.oldActDayOfEntry) {
    assertResidentialFeeDate(args.oldActDayOfEntry);
    facts.oldActDayOfEntry = args.oldActDayOfEntry;
  }
  let hold: ResidentialStayHoldCode | null = null;

  const told = args.dmtaInformedOn;
  if (
    !args.oldActDayOfEntry &&
    args.cohort === 'MTCF_POST_2014' &&
    told !== null &&
    args.date < told &&
    told <= addCalendarMonths(args.stayStartDay, 6)
  ) {
    hold = 'MTCF_OLD_ACT_ENTRY_UNRECORDED';
  }

  const exit = args.exit;
  if (exit !== null) {
    facts.ceasedOn = exit.ceasedOn;
    const informed = args.providerInformedOn ?? args.sourceIssuedAt;
    const exitBeforeInformed = informed === null || informed > exit.ceasedOn;
    if (exit.transitionType === 'DEATH') {
      facts.diedBeforeProviderInformed = exitBeforeInformed;
    } else if (typeof exit.destinationIsResidential === 'boolean') {
      facts.ceasedWithoutStartingAnotherHome = !exit.destinationIsResidential;
    } else if (exitBeforeInformed) {
      hold ??= 'CESSATION_DESTINATION_UNRECORDED';
    } else {
      facts.ceasedWithoutStartingAnotherHome = false;
    }
  }
  return { facts, hold };
}
